import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TCMContent } from './schema';
import { defaultTcmContent } from './demo-content';

const AI_API_URL =
  import.meta.env.VITE_PLATFORM_API_URL ||
  'https://jkd-platform-api-worker.jimsbond007.workers.dev';

export function useTcmContent() {
  const [searchParams] = useSearchParams();
  const isGenerated = searchParams.get('mode') === 'generated';
  const publicId = searchParams.get('publicId');

  const [content, setContent] = useState<TCMContent>(defaultTcmContent);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[useTcmContent] Loading content...', { isGenerated, publicId });

        // Generated 模式：從 localStorage 或 API 載入 AI 生成的內容
        if (isGenerated) {
          console.log('[useTcmContent] Generated mode detected');
          
          // 優先從 localStorage 讀取
          const raw = localStorage.getItem('yoware_generated_content');
          console.log('[useTcmContent] localStorage raw:', raw ? `${raw.substring(0, 100)}...` : 'null');
          
          if (raw) {
            try {
              const parsed = JSON.parse(raw) as TCMContent;
              console.log('[useTcmContent] Successfully parsed generated content:', parsed);
              setContent(parsed);
              setLoading(false);
              return;
            } catch (parseErr) {
              console.error('[useTcmContent] Failed to parse generated content from localStorage:', parseErr);
            }
          } else {
            console.warn('[useTcmContent] No generated content in localStorage');
          }

          // Fallback：從 API 讀取
          if (publicId) {
            console.log('[useTcmContent] Trying to fetch from API with publicId:', publicId);
            const res = await fetch(`${AI_API_URL}/api/orders/${encodeURIComponent(publicId)}/preview`);
            const body = (await res.json().catch(() => ({}))) as {
              success?: boolean;
              data?: { generatedContent?: TCMContent };
              error?: { message?: string };
            };
            console.log('[useTcmContent] API response:', body);
            if (res.ok && body.success !== false && body.data?.generatedContent) {
              console.log('[useTcmContent] Successfully loaded from API');
              setContent(body.data.generatedContent);
              setLoading(false);
              return;
            }
          }

          // 找不到生成內容，使用預設內容
          console.warn('[useTcmContent] Generated content not found, using default content');
          setContent(defaultTcmContent);
          setLoading(false);
          return;
        }

        // 一般模式：從靜態檔案載入
        console.log('[useTcmContent] Normal mode, loading from static file');
        const res = await fetch('/data/content-tcm-v1.json');
        if (!res.ok) {
          throw new Error(`Failed to load content: ${res.status}`);
        }
        const data = (await res.json()) as TCMContent;
        setContent(data);
      } catch (err) {
        console.error('[useTcmContent] Failed to load TCM content:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        // 失敗時使用預設內容
        setContent(defaultTcmContent);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [isGenerated, publicId]);

  const getWhatsAppUrl = useCallback(
    (message?: string) => {
      const text = message || content.siteConfig.whatsappDefaultMessage;
      return `https://wa.me/${content.siteConfig.whatsappNumber}?text=${encodeURIComponent(text)}`;
    },
    [content.siteConfig.whatsappNumber, content.siteConfig.whatsappDefaultMessage],
  );

  return { content, ...content, loading, error, getWhatsAppUrl };
}

export default useTcmContent;
